import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Users2, Pencil, Trash2, Search, Building2, User as UserIcon } from 'lucide-react';
import { api } from '../lib/api';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import PhoneInput from '../components/PhoneInput';
import DocumentInput, { formatDocBR } from '../components/DocumentInput';
import { formatPhoneBR } from '../components/PhoneInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AddressFields, ADDRESS_KEYS } from '../components/AddressFields';

const PERSON_TYPES = { PF: 'Pessoa Física', PJ: 'Pessoa Jurídica' };

const EMPTY_ADDRESS = ADDRESS_KEYS.reduce((acc, k) => { acc[k] = ''; return acc; }, {});

export default function People() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: 'PF',
    name: '',
    document: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    ...EMPTY_ADDRESS,
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get('/people', { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ type: 'PF', name: '', document: '', phone: '', email: '', address: '', notes: '', ...EMPTY_ADDRESS });
    setShow(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      type: p.type || 'PF',
      name: p.name || '',
      document: p.document || '',
      phone: p.phone || '',
      email: p.email || '',
      address: p.address || '',
      notes: p.notes || '',
      ...ADDRESS_KEYS.reduce((acc, k) => { acc[k] = p[k] || ''; return acc; }, {}),
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const payload = {
        type: form.type,
        name: form.name.trim(),
        document: form.document || null,
        phone: form.phone || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      for (const k of ADDRESS_KEYS) {
        payload[k] = (form[k] || '').trim() || null;
      }
      if (editing) {
        await api.put(`/people/${editing.id}`, payload);
      } else {
        await api.post('/people', payload);
      }
      toast.success(editing ? 'Pessoa atualizada' : 'Pessoa cadastrada');
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    try {
      await api.delete(`/people/${p.id}`);
      toast.success('Pessoa excluída');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível excluir');
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <>
      <PageHeader
        title="Pessoas"
        subtitle="Cadastro de pessoas físicas e jurídicas (vendedores, compradores)."
        actions={
          <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="person-new-button">
            <Plus className="h-4 w-4 mr-1.5" /> Nova pessoa
          </Button>
        }
      >
        <form onSubmit={onSearchSubmit} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, CPF/CNPJ, telefone ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 max-w-2xl"
            data-testid="people-search-input"
          />
        </form>
      </PageHeader>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users2}
          title="Nenhuma pessoa cadastrada"
          description="Cadastre PF/PJ que você compra ou vende veículos para reutilizar os dados rapidamente."
          action={{ label: 'Cadastrar pessoa', onClick: openCreate }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="border-border/70" data-testid="person-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted grid place-items-center text-muted-foreground shrink-0">
                    {p.type === 'PJ' ? <Building2 className="h-5 w-5" /> : <UserIcon className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{PERSON_TYPES[p.type]}</div>
                    {p.document && <div className="text-xs text-muted-foreground mt-1 tabular">{formatDocBR(p.document)}</div>}
                    {p.phone && <div className="text-xs text-muted-foreground tabular">{formatPhoneBR(p.phone)}</div>}
                    {p.email && <div className="text-xs text-muted-foreground truncate">{p.email}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid="person-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid="person-delete">
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar pessoa' : 'Nova pessoa'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="person-type-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PF">PF (Pessoa Física)</SelectItem>
                    <SelectItem value="PJ">PJ (Pessoa Jurídica)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.type === 'PJ' ? 'Razão social' : 'Nome'} *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="person-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.type === 'PJ' ? 'CNPJ' : 'CPF'}</Label>
                <DocumentInput value={form.document} onChange={(v) => setForm((f) => ({ ...f, document: v }))} data-testid="person-document-input" />
              </div>
              <div>
                <Label>Telefone</Label>
                <PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} data-testid="person-phone-input" />
              </div>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="person-email-input" />
            </div>
            <div>
              <Label>Endereço (texto livre — legado)</Label>
              <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Mantenha vazio se preencher os campos abaixo" />
            </div>
            <div className="pt-2 border-t">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Endereço estruturado</div>
              <AddressFields
                value={form}
                onChange={(next) => setForm((f) => ({ ...f, ...next }))}
                testIdPrefix="person-addr"
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="person-save-button">
              {editing ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
